import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LENGTH_DEFAULT_RECT, WIDTH_DEFAULT_RECT, MAX_SUM_LENGTH_WIDTH, MIN_CUT_EDGE_LENGTH, } from 'src/app/services/helpers/workbench-helpers/workbench-const-helper';
import { Shape } from 'src/app/models/configurators-2d/applications/workbench/shape';
import { ConfigurationPlan } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { WorkbenchService } from 'src/app/services/configurators-2d/applications/workbench.service';
import { IWorkbenchPreview } from '../../preview/i-workbench-preview';
import { Configurators2DComponent } from '../../../configurators-2d.component';
import { ShapeTypeEnum } from 'src/app/enum/workbench/shape-type.enum';
import { ConstraintGroup } from 'src/app/models/configurators-2d/applications/workbench/constraints/constraint-group';
import { DimensionTypeEnum } from 'src/app/enum/workbench/dimension-type-enum';

@Component({
  selector: 'ng-workbench-plan-sections',
  templateUrl: './plan-sections.component.html',
})
export class WorkbenchPlanSectionsComponent implements OnInit {
  @Input() shapes: Shape[];
  @Input() sectionConfiguration: ConfigurationPlan;
  @Input() collapseIsVisible: boolean;
  @Input() index: number;
  @Output() onSectionSelected: EventEmitter<boolean> = new EventEmitter();

  preview: IWorkbenchPreview;

  shapeContraint: ConstraintGroup = {
    minWidth: Number.POSITIVE_INFINITY,
    maxWidth: Number.NEGATIVE_INFINITY,
    minLength: Number.POSITIVE_INFINITY,
    maxLength: Number.NEGATIVE_INFINITY,
    minDiameter: Number.POSITIVE_INFINITY,
    maxDiameter: Number.NEGATIVE_INFINITY,
  };

  readonly DimensionTypeEnum = DimensionTypeEnum;
  readonly ShapeTypeEnum = ShapeTypeEnum;

  constructor(
    public workbenchService: WorkbenchService,
    configurators2DComponent: Configurators2DComponent
  ) {
    this.preview = configurators2DComponent.preview as IWorkbenchPreview;
  }

  ngOnInit(): void {
    if (this.sectionConfiguration.shapeId == null && this.shapes) {
      this.selectShape(this.shapes.find(shape => shape.shapeType == ShapeTypeEnum.Rectangle));
    } else {
      // Min / Max
      this.getMinMaxDimensions(this.sectionConfiguration.shape.constraintGroups);
    }
  }

  /**
   * Fonction de sélection de le forme
   * @param shape
   */
  async selectShape(shape: Shape) {
    this.preview.removePlan(this.sectionConfiguration.displayOrder);
    this.sectionConfiguration.shape = shape;
    this.sectionConfiguration.shapeId = shape.id;

    // Reset dim
    this.sectionConfiguration.diameter = null;
    this.sectionConfiguration.width = null;
    this.sectionConfiguration.length = null;

    // Reset visual configurations (edges, jambs, cutaways, splashbacks)
    this.sectionConfiguration.configurationEdges = [];
    this.sectionConfiguration.configurationJambs = [];
    this.sectionConfiguration.configurationCutaways = [];
    this.sectionConfiguration.configurationSplashbacks = [];

    // Min / Max
    this.getMinMaxDimensions(this.sectionConfiguration.shape.constraintGroups);

    // valeur par défaut de la forme
    const widthDefaultRect = WIDTH_DEFAULT_RECT;
    const lenghtDefaultRect = LENGTH_DEFAULT_RECT;
    const widthDefaultHalfMoon = 815;
    const lenghtDefaultHalfMoon = 2310;
    const widthDefaultMushroom = 815;
    const lenghtDefaultMushroom = 2560;
    const diameterDefaultMushroom = 915;
    const widthDefaultAngle = 663;
    const lenghtDefaultAngle = 925;
    const diameterDefaultCircle = 940;

    switch (this.sectionConfiguration.shape.shapeType) {
      case ShapeTypeEnum.Rectangle:
        this.sectionConfiguration.width = widthDefaultRect;
        this.sectionConfiguration.length = lenghtDefaultRect;
        break;
      case ShapeTypeEnum.HalfMoon:
        this.sectionConfiguration.width = widthDefaultHalfMoon;
        this.sectionConfiguration.length = lenghtDefaultHalfMoon;
        break;
      case ShapeTypeEnum.Mushroom:
        this.sectionConfiguration.width = widthDefaultMushroom;
        this.sectionConfiguration.length = lenghtDefaultMushroom;
        this.sectionConfiguration.diameter = diameterDefaultMushroom;
        break;
      case ShapeTypeEnum.Angle:
        this.sectionConfiguration.length = lenghtDefaultAngle;
        this.sectionConfiguration.width = widthDefaultAngle;
        break;
      case ShapeTypeEnum.Circle:
        this.sectionConfiguration.diameter = diameterDefaultCircle;
        break;
      default:
        break;
    }

    await this.addPreviewPlan();
  }

  /**
   * Fonction d'affichage de la sélection de la forme
   * @param id de la forme
   * @returns boolean
   */
  isShapeSelected(id: number): boolean {
    return this.sectionConfiguration.shape?.id == id;
  }

  /**
   * Vérifie si on a un groupe de contraintes pour chaque dimension.
   */
  async checkDimensions(dimensionType: DimensionTypeEnum): Promise<void> {
    let cgs = this.sectionConfiguration.shape.constraintGroups.filter(
      cg =>
        this.sectionConfiguration.width >= cg.minWidth &&
        this.sectionConfiguration.width <= cg.maxWidth &&
        this.sectionConfiguration.length >= cg.minLength &&
        this.sectionConfiguration.length <= cg.maxLength &&
        this.sectionConfiguration.diameter >= cg.minDiameter &&
        this.sectionConfiguration.diameter <= cg.maxDiameter
    );

    switch (this.sectionConfiguration.shape.shapeType) {
      // application des contraintes spécifiques des dimensions de plan d'angle
      case ShapeTypeEnum.Angle:
        this.getMinMaxDimensions(cgs);

        if (cgs.length > 0) {
          this.checkAndUpdateAngleDimensions(dimensionType);
          await this.addPreviewPlan();
        }
        break;

      default:
        if (cgs.length === 0) {
          if (this.sectionConfiguration.length > this.sectionConfiguration.width) {
            cgs = this.sectionConfiguration.shape.constraintGroups.filter(
              cg => this.sectionConfiguration.length >= cg.minLength && this.sectionConfiguration.length <= cg.maxLength
            );
          } else {
            cgs = this.sectionConfiguration.shape.constraintGroups.filter(
              cg => this.sectionConfiguration.width >= cg.minWidth && this.sectionConfiguration.width <= cg.maxWidth
            );
          }
        } else {
          await this.addPreviewPlan();
        }

        this.getMinMaxDimensions(cgs);
    }
  }

  /**
   * Si plan d'angle, on vérifie que les dimensions sont valides et si besoin, on les modifie
   * @param dimensionType
   */
  checkAndUpdateAngleDimensions(dimensionType: DimensionTypeEnum) {
    // Si ce n'est pas un plan d'angle, on ne recalcule pas la longueur ou la largeur
    if (this.sectionConfiguration.shape.shapeType !== ShapeTypeEnum.Angle) {
      return;
    }

    //on modifie l'autre valeur
    switch (dimensionType) {
      //si la longueur vient d'être modifiée, on recalcule la largeur
      case DimensionTypeEnum.Length: {
        let computedWidth: number[] = [];

        // calcul de la largueur max
        // vérification de la règle longueur + largeur <= 1600
        let tmp = MAX_SUM_LENGTH_WIDTH - this.sectionConfiguration.length;
        if (this.shapeContraint.maxWidth > tmp) {
          computedWidth.push(tmp);
        }

        // vérification de la règle longueur pan coupé >= 80
        tmp = Math.round(this.sectionConfiguration.length - Math.sqrt((MIN_CUT_EDGE_LENGTH * MIN_CUT_EDGE_LENGTH) / 2));
        if (this.shapeContraint.maxWidth > tmp) {
          computedWidth.push(tmp);
        }

        const maxWidth = Math.min(...computedWidth);
        if (this.shapeContraint.maxWidth > maxWidth) {
          this.shapeContraint.maxWidth = maxWidth;
        }

        // vérification que la largeur soit entre le min et le max
        if (this.sectionConfiguration.width < this.shapeContraint.minWidth) {
          this.sectionConfiguration.width = this.shapeContraint.minWidth;
        } else if (this.sectionConfiguration.width > this.shapeContraint.maxWidth) {
          this.sectionConfiguration.width = this.shapeContraint.maxWidth;
        }
        break;
      }
      // si la largeur vient d'être modifiée, on recalcule la longueur
      case DimensionTypeEnum.Width: {
        let computedLength: number[] = [];

        // calcul de la longueur max
        //vérification de la règle longueur + largeur <= 1600
        let tmp = MAX_SUM_LENGTH_WIDTH - this.sectionConfiguration.width;
        if (this.shapeContraint.maxLength > tmp) {
          computedLength.push(tmp);
        }

        //vérification de la règle longueur pan coupé >= 80
        tmp = Math.round(this.sectionConfiguration.width + Math.sqrt((MIN_CUT_EDGE_LENGTH * MIN_CUT_EDGE_LENGTH) / 2));
        if (this.shapeContraint.maxLength > tmp) {
          computedLength.push(tmp);
        }

        const maxLength = Math.max(...computedLength);
        if (this.shapeContraint.maxLength > maxLength) {
          this.shapeContraint.maxLength = maxLength;
        }

        // vérification que la largeur soit entre le min et le max
        if (this.sectionConfiguration.length < this.shapeContraint.minLength) {
          this.sectionConfiguration.length = this.shapeContraint.minLength;
        } else if (this.sectionConfiguration.length > this.shapeContraint.maxLength) {
          this.sectionConfiguration.length = this.shapeContraint.maxLength;
        }
        break;
      }
    }
  }

  toggleCollapse(): void {
    this.collapseIsVisible = !this.collapseIsVisible;
    this.onSectionSelected.emit(this.collapseIsVisible);
  }

  /**
   * Retire un plan
   * @param index
   */
  deleteSection(index) {
    this.preview.deletePlan();
    this.workbenchService.configuration.workbenchConfigurationPlans.splice(
      this.workbenchService.configuration.workbenchConfigurationPlans.findIndex(x => x.displayOrder == index),
      1
    );
    this.workbenchService.configuration.workbenchConfigurationPlans.forEach((plan, index) => {
      plan.displayOrder = index + 1;
    });
    this.onSectionSelected.emit(false);
  }

  getSquareMeter(): number | null {
    if (this.sectionConfiguration.shape == null) {
      return null;
    }

    let surface: number;

    switch (this.sectionConfiguration.shape.shapeType) {
      case ShapeTypeEnum.HalfMoon: // Le plan demi-lune est calculé comme un plan rectangle
      case ShapeTypeEnum.Rectangle: {
        surface = this.sectionConfiguration.length * this.sectionConfiguration.width;
        break;
      }
      case ShapeTypeEnum.Mushroom: {
        // L'aire du plan champignon est l'aire du rectangle englobant le plan
        surface = this.sectionConfiguration.length * this.sectionConfiguration.diameter;
        break;
      }
      case ShapeTypeEnum.Angle: {
        // Le plan d'angle est calculé comme un plan carré
        surface = this.sectionConfiguration.length ** 2;
        break;
      }
      case ShapeTypeEnum.Circle: {
        // Le plan rond est calculé comme un plan carré
        surface = this.sectionConfiguration.diameter ** 2;
        break;
      }
      default:
        return null;
    }

    return Math.round((surface / 1000000) * 100) / 100;
  }

  /**
   * Récupère les bornes min et max de chaque forme
   */
  private getMinMaxDimensions(constraintGroups?: ConstraintGroup[]): void {
    if (constraintGroups.length > 0) {
      // récupération des plus petits min et des plus grands max
      this.shapeContraint = {
        minWidth: Math.min(...constraintGroups.map(c => c.minWidth)),
        maxWidth: Math.max(...constraintGroups.map(c => c.maxWidth)),
        minLength: Math.min(...constraintGroups.map(c => c.minLength)),
        maxLength: Math.max(...constraintGroups.map(c => c.maxLength)),
        minDiameter: Math.min(...constraintGroups.map(c => c.minDiameter)),
        maxDiameter: Math.max(...constraintGroups.map(c => c.maxDiameter)),
      };

      switch (this.sectionConfiguration.shape.shapeType) {
        case ShapeTypeEnum.Mushroom:
          if (this.sectionConfiguration.width != null) {
            this.shapeContraint.minDiameter = this.sectionConfiguration.width;
          }
          break;
        default:
          break;
      }
    }
  }

  /**
   * Flip la forme si c'est un plan champignon.
   */
  flipShape(sectionConfiguration: ConfigurationPlan): void {
    if (sectionConfiguration?.shape?.shapeType === ShapeTypeEnum.Mushroom) {
      this.preview.flipMushroomShape(sectionConfiguration);
    }
  }

  /**
   * Affiche un plan dans la preview
   * @param shapeType
   */
  private async addPreviewPlan(): Promise<void> {
    this.preview.addOrUpdateShape();
    await this.preview.recreateEdgesCutawaysAndJambs();
  }
}
